/**
 * The API client.
 *
 * **The SPA talks only to the API and to Firebase** (Spec §2.3). It never reaches
 * workers, queues, or the database. Tiles are the one exception: static PMTiles
 * come from CloudFront, handled in `map.ts`.
 *
 * Every request carries a fresh Firebase ID token. Tokens last an hour, and
 * `getIdToken()` refreshes transparently when one is close to expiring, so this
 * client asks for a token per request rather than caching one.
 *
 * `request`/`get`/`post` are the original edge: they throw a bare `ApiError`
 * and hand back `unknown`. `getParsed`/`postParsed`/`putParsed`/`patchParsed`/`del`/`delParsed` are the
 * typed edge: they throw `ApiFailureError` from the taxonomy in
 * `./http/errors` and parse the body against a caller-supplied Zod schema
 * through `./http/parse` before returning it. This file still imports no
 * React and defines no schema of its own — `ZodType` is a type-only import,
 * and every schema comes in as an argument.
 */

import type { ZodType } from 'zod';

import { ApiFailureError, toFailure } from './http/errors';
import { ParseError, parseOrThrow } from './http/parse';
import { reloadIfRedeployed } from './http/staleBundle';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Supplies a current Firebase ID token, or null when signed out. */
export type TokenSource = () => Promise<string | null>;

/**
 * The subset of `fetch` this client uses. Narrower than `typeof fetch`, whose
 * overloads make it awkward to substitute a test double.
 */
export type FetchLike = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

export class ApiClient {
  constructor(
    private readonly baseUrl: string,
    private readonly getToken: TokenSource,
    // Not `= fetch`: that stores the native, receiver-branded function and every
    // call below invokes it as `this.fetchImpl(...)`, whose receiver is this
    // `ApiClient` instance rather than `window` -- `fetch` throws
    // "Illegal invocation" for any receiver that isn't the global object. The
    // wrapper calls it unqualified, which the browser treats as a call on the
    // global scope.
    private readonly fetchImpl: FetchLike = (...args) => fetch(...args),
  ) {}

  async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const token = await this.getToken();
    if (!token) {
      throw new ApiError(401, 'not signed in');
    }

    const response = await this.fetchImpl(`${this.baseUrl}${path}`, {
      ...init,
      headers: {
        ...init.headers,
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new ApiError(response.status, `${init.method ?? 'GET'} ${path} failed`);
    }

    // 204 has no body to parse.
    if (response.status === 204) {
      return undefined as T;
    }
    return (await response.json()) as T;
  }

  get<T>(path: string): Promise<T> {
    return this.request<T>(path, { method: 'GET' });
  }

  post<T>(path: string, body: unknown): Promise<T> {
    return this.request<T>(path, { method: 'POST', body: JSON.stringify(body) });
  }

  /**
   * Performs the fetch and returns the raw status and body without throwing
   * on a non-2xx response — `request()` above still does that, unchanged,
   * for its own callers. This is a separate, private code path so the typed
   * methods below can build an `ApiFailure` from the real body rather than
   * from `request()`'s generic "GET /path failed" message.
   */
  private async raw(path: string, init: RequestInit): Promise<{ status: number; body: unknown }> {
    const token = await this.getToken();
    if (!token) {
      throw new ApiFailureError({ kind: 'unauthenticated' });
    }

    let response: Response;
    try {
      response = await this.fetchImpl(`${this.baseUrl}${path}`, {
        ...init,
        headers: {
          ...init.headers,
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
    } catch {
      // fetch() rejects on a network failure (offline, DNS, CORS) — it never
      // rejects for a non-2xx status, which arrives as a normal Response.
      throw new ApiFailureError({ kind: 'offline' });
    }

    // A route this bundle calls that the api no longer serves for the verb: the api moved on.
    if (response.status === 405) {
      void reloadIfRedeployed();
    }
    if (response.status === 204) {
      return { status: response.status, body: undefined };
    }

    const text = await response.text();
    if (text.length === 0) {
      return { status: response.status, body: undefined };
    }
    try {
      return { status: response.status, body: JSON.parse(text) as unknown };
    } catch {
      // Not JSON (an HTML error page from a proxy, a plain-text 502, ...).
      // toFailure() below still gets a status to map even when the body
      // carries nothing structured.
      return { status: response.status, body: text };
    }
  }

  private async requestParsed<T>(path: string, init: RequestInit, schema: ZodType<T>): Promise<T> {
    const { status, body } = await this.raw(path, init);
    if (status < 200 || status >= 300) {
      throw new ApiFailureError(toFailure(status, body));
    }
    try {
      return parseOrThrow(schema, body, path);
    } catch (error) {
      if (error instanceof ParseError) {
        void reloadIfRedeployed();
        throw new ApiFailureError({ kind: 'malformed', issues: error.issues });
      }
      throw error;
    }
  }

  getParsed<T>(path: string, schema: ZodType<T>): Promise<T> {
    return this.requestParsed(path, { method: 'GET' }, schema);
  }

  postParsed<T>(path: string, body: unknown, schema: ZodType<T>): Promise<T> {
    return this.requestParsed(path, { method: 'POST', body: JSON.stringify(body) }, schema);
  }

  putParsed<T>(path: string, body: unknown, schema: ZodType<T>): Promise<T> {
    return this.requestParsed(path, { method: 'PUT', body: JSON.stringify(body) }, schema);
  }

  patchParsed<T>(path: string, body: unknown, schema: ZodType<T>): Promise<T> {
    return this.requestParsed(path, { method: 'PATCH', body: JSON.stringify(body) }, schema);
  }

  delParsed<T>(path: string, schema: ZodType<T>): Promise<T> {
    return this.requestParsed(path, { method: 'DELETE' }, schema);
  }

  async del(path: string): Promise<void> {
    const { status, body } = await this.raw(path, { method: 'DELETE' });
    if (status < 200 || status >= 300) {
      throw new ApiFailureError(toFailure(status, body));
    }
  }
}
